import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Bulletin,
  BulletinCategory,
  BulletinPriority,
  BulletinTargetType,
  BulletinMediaItem,
} from './AdminBulletinPage';

export interface StaffIncidentItem {
  id: string;
  category: BulletinCategory;
  priority: BulletinPriority;
  title: string;
  summary: string;
  content: string;
  media_files?: BulletinMediaItem[];
  created_at: string;
  author_name: string;
  require_ack: boolean;
  target_type: BulletinTargetType;
  is_read: boolean;
  read_at: string | null;
  is_acknowledged: boolean;
  acknowledged_at: string | null;
}

export interface StaffDisciplineStats {
  staff_id: string;
  staff_name: string;
  role: string | null;
  color: string | null;
  status: string;
  total_incidents: number;
  violation_count: number;
  reward_count: number;
  urgent_violation_count: number;
  acknowledged_count: number;
  unacknowledged_count: number;
  latest_incident_date: string | null;
  incidents: StaffIncidentItem[];
}

export interface DisciplineRewardSummary {
  total_staff: number;
  staff_with_violations: number;
  staff_with_rewards: number;
  clean_staff_count: number;
  compliance_rate: number;
  total_violations: number;
  total_rewards: number;
  urgent_violations: number;
  total_required_acks: number;
  total_completed_acks: number;
  ack_rate: number;
  pending_acks_count: number;
}

export interface TimelineStat {
  period: string;
  violations: number;
  rewards: number;
}

interface StaffMember {
  id: string;
  name: string;
  role?: string;
  status?: string;
}

const CATEGORY_META = {
  vi_pham: {
    label: 'Vi phạm / Nhắc nhở',
    icon: 'fas fa-exclamation-triangle',
    color: '#c0392b',
    bg: 'rgba(192, 57, 43, 0.1)',
  },
  khen_thuong: {
    label: 'Khen thưởng / Vinh danh',
    icon: 'fas fa-award',
    color: '#b7791f',
    bg: 'rgba(183, 121, 31, 0.12)',
  },
};

const PRIORITY_META = {
  normal: { label: 'Bình thường', color: '#726969' },
  important: { label: 'Quan trọng', color: '#b7791f' },
  urgent: { label: 'Khẩn cấp', color: '#8d2a2c' },
};

// Date range helpers
function getPresetDateRange(preset: string): { start: string; end: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed
  const d = now.getDate();

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (preset === 'today') {
    const dateStr = formatDate(now);
    return { start: dateStr, end: dateStr };
  }

  if (preset === 'this_week') {
    const dayOfWeek = now.getDay() || 7; // Monday = 1, Sunday = 7
    const monday = new Date(now);
    monday.setDate(d - (dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: formatDate(monday), end: formatDate(sunday) };
  }

  if (preset === 'this_month') {
    const firstDay = new Date(y, m, 1);
    const lastDay = new Date(y, m + 1, 0);
    return { start: formatDate(firstDay), end: formatDate(lastDay) };
  }

  if (preset === 'last_month') {
    const firstDay = new Date(y, m - 1, 1);
    const lastDay = new Date(y, m, 0);
    return { start: formatDate(firstDay), end: formatDate(lastDay) };
  }

  if (preset === 'this_quarter') {
    const quarter = Math.floor(m / 3);
    const firstDay = new Date(y, quarter * 3, 1);
    const lastDay = new Date(y, (quarter + 1) * 3, 0);
    return { start: formatDate(firstDay), end: formatDate(lastDay) };
  }

  if (preset === 'this_year') {
    const firstDay = new Date(y, 0, 1);
    const lastDay = new Date(y, 12, 0);
    return { start: formatDate(firstDay), end: formatDate(lastDay) };
  }

  return { start: '', end: '' };
}

export const DisciplineRewardStatsPage: React.FC = () => {
  const { user } = useAuth();

  // Primary data state
  const [summary, setSummary] = useState<DisciplineRewardSummary | null>(null);
  const [staffStats, setStaffStats] = useState<StaffDisciplineStats[]>([]);
  const [incidents, setIncidents] = useState<Bulletin[]>([]);
  const [timelineStats, setTimelineStats] = useState<TimelineStat[]>([]);
  const [allStaffList, setAllStaffList] = useState<StaffMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [timePreset, setTimePreset] = useState<string>('this_month');
  const [startDate, setStartDate] = useState<string>(() => getPresetDateRange('this_month').start);
  const [endDate, setEndDate] = useState<string>(() => getPresetDateRange('this_month').end);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [ackFilter, setAckFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Active view tab: 'staff_view' vs 'incidents_view'
  const [activeTab, setActiveTab] = useState<'staff_view' | 'incidents_view'>('staff_view');

  // Staff sorting
  const [staffSortBy, setStaffSortBy] = useState<'violations' | 'rewards' | 'total' | 'name'>('violations');
  const [staffSortOrder, setStaffSortOrder] = useState<'desc' | 'asc'>('desc');

  // Modals state
  const [selectedStaffForModal, setSelectedStaffForModal] = useState<StaffDisciplineStats | null>(null);
  const [selectedBulletinForModal, setSelectedBulletinForModal] = useState<Bulletin | null>(null);
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);

  // Quick Create Form State
  const [createCategory, setCreateCategory] = useState<'vi_pham' | 'khen_thuong'>('vi_pham');
  const [createPriority, setCreatePriority] = useState<BulletinPriority>('normal');
  const [createTitle, setCreateTitle] = useState('');
  const [createSummary, setCreateSummary] = useState('');
  const [createContent, setCreateContent] = useState('');
  const [createTargetType, setCreateTargetType] = useState<BulletinTargetType>('specific');
  const [createRecipientIds, setCreateRecipientIds] = useState<string[]>([]);
  const [createRequireAck, setCreateRequireAck] = useState(true);
  const [createStaffSearch, setCreateStaffSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Auth headers
  const authHeaders = useMemo(() => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('auth_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, [user?.id]);

  // Load staff list for filter and selector
  const loadStaffList = useCallback(async () => {
    try {
      const res = await fetch('/api/staff?all=true', { headers: authHeaders });
      const data = await res.json();
      if (data.success) {
        setAllStaffList(data.data || []);
      }
    } catch {
      // ignore
    }
  }, [authHeaders]);

  // Fetch statistics from backend
  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);

      const res = await fetch(`/api/bulletins/discipline-rewards/stats?${params.toString()}`, {
        headers: authHeaders,
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Không thể tải thống kê kỷ luật & khen thưởng');
      }

      setSummary(data.data.summary);
      setStaffStats(data.data.staff_stats || []);
      setIncidents(data.data.incidents || []);
      setTimelineStats(data.data.timeline_stats || []);
    } catch (err: any) {
      setError(err.message || 'Lỗi không xác định khi tải dữ liệu');
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders, startDate, endDate, categoryFilter]);

  useEffect(() => {
    loadStaffList();
  }, [loadStaffList]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Handle Preset Time Selection
  const handleSelectTimePreset = (preset: string) => {
    setTimePreset(preset);
    if (preset === 'all') {
      setStartDate('');
      setEndDate('');
    } else {
      const range = getPresetDateRange(preset);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  };

  // Filtered & Sorted Staff List
  const filteredStaffStats = useMemo(() => {
    let list = [...staffStats];

    // Filter by role
    if (roleFilter !== 'all') {
      list = list.filter((s) => s.role === roleFilter);
    }

    // Filter by search query (name or staff_id)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (s) =>
          s.staff_name.toLowerCase().includes(q) ||
          s.staff_id.toLowerCase().includes(q) ||
          (s.role && s.role.toLowerCase().includes(q))
      );
    }

    // Filter by acknowledgment status
    if (ackFilter === 'pending') {
      list = list.filter((s) => s.unacknowledged_count > 0);
    } else if (ackFilter === 'completed') {
      list = list.filter((s) => s.violation_count > 0 && s.unacknowledged_count === 0);
    }

    // Sort
    list.sort((a, b) => {
      let valA = 0;
      let valB = 0;
      if (staffSortBy === 'violations') {
        valA = a.violation_count;
        valB = b.violation_count;
      } else if (staffSortBy === 'rewards') {
        valA = a.reward_count;
        valB = b.reward_count;
      } else if (staffSortBy === 'total') {
        valA = a.total_incidents;
        valB = b.total_incidents;
      } else if (staffSortBy === 'name') {
        return staffSortOrder === 'asc'
          ? a.staff_name.localeCompare(b.staff_name)
          : b.staff_name.localeCompare(a.staff_name);
      }

      if (staffSortOrder === 'desc') {
        return valB - valA || a.staff_name.localeCompare(b.staff_name);
      } else {
        return valA - valB || a.staff_name.localeCompare(b.staff_name);
      }
    });

    return list;
  }, [staffStats, roleFilter, searchQuery, ackFilter, staffSortBy, staffSortOrder]);

  // Filtered Incidents List
  const filteredIncidents = useMemo(() => {
    let list = [...incidents];

    if (categoryFilter !== 'all') {
      list = list.filter((item) => item.category === categoryFilter);
    }

    if (priorityFilter !== 'all') {
      list = list.filter((item) => item.priority === priorityFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          (item.summary && item.summary.toLowerCase().includes(q)) ||
          item.author_name.toLowerCase().includes(q) ||
          (item.recipient_names && item.recipient_names.some((name) => name.toLowerCase().includes(q)))
      );
    }

    return list;
  }, [incidents, categoryFilter, priorityFilter, searchQuery]);

  // Top 5 Commended Staff
  const topCommendedStaff = useMemo(() => {
    return [...staffStats]
      .filter((s) => s.reward_count > 0)
      .sort((a, b) => b.reward_count - a.reward_count || a.violation_count - b.violation_count)
      .slice(0, 5);
  }, [staffStats]);

  // Top 5 Violations Staff (Most infractions)
  const topViolationsStaff = useMemo(() => {
    return [...staffStats]
      .filter((s) => s.violation_count > 0)
      .sort((a, b) => b.violation_count - a.violation_count || b.urgent_violation_count - a.urgent_violation_count)
      .slice(0, 5);
  }, [staffStats]);

  // Format Date Display (DD/MM/YYYY)
  const formatDateVN = (dateString?: string | null) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    } catch {
      return dateString;
    }
  };

  const formatDateTimeVN = (dateString?: string | null) => {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    } catch {
      return dateString;
    }
  };

  // Staff Thi Đua Classification
  const getStaffEvaluation = (staff: StaffDisciplineStats) => {
    if (staff.reward_count >= 1 && staff.violation_count === 0) {
      return { label: 'Xuất sắc', color: '#b7791f', bg: 'rgba(183, 121, 31, 0.12)', icon: 'fas fa-trophy' };
    }
    if (staff.violation_count === 0) {
      return { label: 'Tốt (Chuẩn mực)', color: '#1e7e48', bg: 'rgba(30, 126, 72, 0.12)', icon: 'fas fa-check-circle' };
    }
    if (staff.violation_count <= 2) {
      return { label: 'Cần nhắc nhở', color: '#d97706', bg: 'rgba(217, 119, 6, 0.12)', icon: 'fas fa-exclamation-circle' };
    }
    return { label: 'Cảnh báo kỷ luật', color: '#c0392b', bg: 'rgba(192, 57, 43, 0.15)', icon: 'fas fa-skull-crossbones' };
  };

  // Export to Excel (CSV UTF-8 with BOM)
  const handleExportCSV = () => {
    if (activeTab === 'staff_view') {
      const headers = [
        'Mã Nhân Viên',
        'Họ và Tên',
        'Bộ phận',
        'Số lỗi Vi phạm',
        'Số lỗi Khẩn cấp',
        'Số Khen thưởng',
        'Đã ký xác nhận',
        'Chưa ký nhận',
        'Tỷ lệ ký (%)',
        'Đánh giá thi đua',
        'Sự vụ gần nhất',
      ];

      const rows = filteredStaffStats.map((s) => {
        const totalAcks = s.acknowledged_count + s.unacknowledged_count;
        const ackPercent = totalAcks > 0 ? Math.round((s.acknowledged_count / totalAcks) * 100) : 100;
        return [
          `"${s.staff_id}"`,
          `"${s.staff_name}"`,
          `"${s.role || '-'}"`,
          s.violation_count,
          s.urgent_violation_count,
          s.reward_count,
          s.acknowledged_count,
          s.unacknowledged_count,
          `${ackPercent}%`,
          `"${getStaffEvaluation(s).label}"`,
          `"${formatDateVN(s.latest_incident_date)}"`,
        ].join(',');
      });

      const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Thong_Ke_Ky_Luat_Khen_Thuong_Nhan_Vien_${startDate || 'all'}_${endDate || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      const headers = [
        'Mã sự vụ',
        'Ngày ban hành',
        'Phân loại',
        'Mức độ',
        'Tiêu đề',
        'Tóm tắt',
        'Đối tượng nhận',
        'Người ban hành',
        'Đã xem',
        'Đã ký xác nhận',
        'Yêu cầu xác nhận',
      ];

      const rows = filteredIncidents.map((inc) => {
        return [
          `"${inc.id}"`,
          `"${formatDateVN(inc.created_at)}"`,
          `"${inc.category === 'vi_pham' ? 'Vi phạm / Nhắc nhở' : 'Khen thưởng / Vinh danh'}"`,
          `"${PRIORITY_META[inc.priority]?.label || inc.priority}"`,
          `"${inc.title.replace(/"/g, '""')}"`,
          `"${(inc.summary || '').replace(/"/g, '""')}"`,
          `"${inc.target_type === 'all' ? 'Toàn thể nhân viên' : (inc.recipient_names || []).join('; ')}"`,
          `"${inc.author_name}"`,
          `${inc.read_count || 0}/${inc.total_recipients || 0}`,
          `${inc.ack_count || 0}/${inc.total_recipients || 0}`,
          `"${inc.require_ack ? 'Có' : 'Không'}"`,
        ].join(',');
      });

      const csvContent = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Nhat_Ky_Vi_Pham_Khen_Thuong_${startDate || 'all'}_${endDate || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Quick Create Submit
  const handleQuickCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createTitle.trim()) {
      alert('Vui lòng nhập tiêu đề!');
      return;
    }
    if (!createContent.trim()) {
      alert('Vui lòng nhập nội dung chi tiết!');
      return;
    }
    if (createTargetType === 'specific' && createRecipientIds.length === 0) {
      alert('Vui lòng chọn ít nhất một nhân viên!');
      return;
    }

    try {
      setIsCreating(true);
      const payload = {
        category: createCategory,
        priority: createPriority,
        title: createTitle.trim(),
        summary: createSummary.trim() || undefined,
        content: createContent.trim(),
        target_type: createTargetType,
        recipient_ids: createTargetType === 'specific' ? createRecipientIds : [],
        require_ack: createRequireAck,
        status: 'published',
      };

      const res = await fetch('/api/bulletins', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Không thể tạo bản ghi mới');
      }

      alert('Đã tạo ghi nhận thành công!');
      setIsQuickCreateOpen(false);
      // Reset form
      setCreateTitle('');
      setCreateSummary('');
      setCreateContent('');
      setCreateRecipientIds([]);
      fetchStats();
    } catch (err: any) {
      alert(err.message || 'Lỗi khi tạo bản ghi');
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleRecipient = (staffId: string) => {
    setCreateRecipientIds((prev) =>
      prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]
    );
  };

  const filteredCreateStaffList = useMemo(() => {
    const activeStaff = allStaffList.filter((s) => s.status !== 'Đã nghỉ');
    if (!createStaffSearch.trim()) return activeStaff;
    const q = createStaffSearch.toLowerCase().trim();
    return activeStaff.filter(
      (s) => s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q) || (s.role && s.role.toLowerCase().includes(q))
    );
  }, [allStaffList, createStaffSearch]);

  // Max value in timeline chart for scaling
  const maxTimelineCount = useMemo(() => {
    if (!timelineStats.length) return 5;
    const maxVal = Math.max(...timelineStats.map((t) => Math.max(t.violations, t.rewards)));
    return maxVal < 5 ? 5 : maxVal;
  }, [timelineStats]);

  return (
    <div className="bulletin-admin-container discipline-stats-page">
      {/* Top Header */}
      <div className="bulletin-admin-header discipline-header">
        <div>
          <div className="discipline-header-breadcrumb">
            <Link to="/admin-bulletins" className="back-link">
              <i className="fas fa-arrow-left"></i> Bảng Tin Nội Bộ
            </Link>
            <span className="divider">/</span>
            <span className="current">Thống Kê Kỷ Luật & Khen Thưởng</span>
          </div>
          <h1 className="bulletin-admin-title" style={{ marginTop: '8px' }}>
            <i className="fas fa-scale-balanced" style={{ color: '#8d2a2c', marginRight: '12px' }}></i>
            Thống Kê Vi Phạm & Khen Thưởng Nhân Viên
          </h1>
          <p className="bulletin-admin-subtitle">
            Theo dõi, tổng hợp và xếp hạng mức độ tuân thủ quy chế, các lỗi vi phạm và thành tích khen thưởng của từng nhân sự.
          </p>
        </div>

        <div className="discipline-header-actions">
          <button
            className="bulletin-btn-create"
            onClick={() => {
              setCreateCategory('vi_pham');
              setIsQuickCreateOpen(true);
            }}
          >
            <i className="fas fa-plus-circle" style={{ marginRight: '8px' }}></i>
            Tạo Ghi Nhận Mới
          </button>
          <button className="btn-secondary-action" onClick={handleExportCSV} title="Xuất file Excel (CSV UTF-8)">
            <i className="fas fa-file-excel" style={{ color: '#1e7e48', marginRight: '6px' }}></i>
            Xuất Excel
          </button>
          <button className="btn-secondary-action" onClick={() => window.print()} title="In báo cáo hoặc lưu PDF">
            <i className="fas fa-print" style={{ marginRight: '6px' }}></i>
            In Báo Cáo
          </button>
          <button className="btn-icon-action" onClick={fetchStats} title="Làm mới dữ liệu">
            <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* FILTER TOOLBAR */}
      <div className="discipline-filter-panel">
        {/* Row 1: Time Preset Badges */}
        <div className="time-preset-row">
          <span className="filter-label">
            <i className="far fa-calendar-alt"></i> Thời gian:
          </span>
          <div className="preset-buttons-group">
            {[
              { id: 'all', label: 'Tất cả thời gian' },
              { id: 'today', label: 'Hôm nay' },
              { id: 'this_week', label: 'Tuần này' },
              { id: 'this_month', label: 'Tháng này' },
              { id: 'last_month', label: 'Tháng trước' },
              { id: 'this_quarter', label: 'Quý này' },
              { id: 'this_year', label: 'Năm nay' },
              { id: 'custom', label: 'Tùy chỉnh' },
            ].map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={`preset-btn ${timePreset === preset.id ? 'active' : ''}`}
                onClick={() => handleSelectTimePreset(preset.id)}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Custom Date Inputs (shown if custom preset or always editable) */}
          {timePreset === 'custom' && (
            <div className="custom-date-inputs">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                title="Từ ngày"
              />
              <span className="range-to">đến</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                title="Đến ngày"
              />
            </div>
          )}
        </div>

        {/* Row 2: Category, Role, Priority & Search */}
        <div className="filter-row-secondary">
          <div className="filter-select-item">
            <label>Phân loại:</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">Tất cả (Lỗi & Thưởng)</option>
              <option value="vi_pham">⚠️ Chỉ Vi phạm / Nhắc nhở</option>
              <option value="khen_thuong">🏆 Chỉ Khen thưởng / Vinh danh</option>
            </select>
          </div>

          <div className="filter-select-item">
            <label>Bộ phận:</label>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">Tất cả bộ phận</option>
              <option value="CSKH">Chăm sóc khách hàng (CSKH)</option>
              <option value="SX_AO">Sản xuất áo</option>
              <option value="SX_THE">Sản xuất thẻ</option>
            </select>
          </div>

          {activeTab === 'incidents_view' && (
            <div className="filter-select-item">
              <label>Mức độ:</label>
              <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
                <option value="all">Tất cả mức độ</option>
                <option value="normal">Bình thường</option>
                <option value="important">Quan trọng</option>
                <option value="urgent">Khẩn cấp</option>
              </select>
            </div>
          )}

          {activeTab === 'staff_view' && (
            <div className="filter-select-item">
              <label>Tình trạng ký nhận:</label>
              <select value={ackFilter} onChange={(e) => setAckFilter(e.target.value)}>
                <option value="all">Tất cả trạng thái</option>
                <option value="pending">⚠️ Còn biên bản chưa ký</option>
                <option value="completed">✅ Đã ký nhận đủ 100%</option>
              </select>
            </div>
          )}

          <div className="filter-search-box">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder={activeTab === 'staff_view' ? 'Tìm nhân viên theo tên, mã NV...' : 'Tìm theo tiêu đề sự vụ, người tạo...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* TOP KPI CARDS */}
      <div className="discipline-kpi-grid">
        {/* KPI 1: Total Violations */}
        <div className="kpi-card danger-card">
          <div className="kpi-icon-wrap" style={{ background: 'rgba(192, 57, 43, 0.12)', color: '#c0392b' }}>
            <i className="fas fa-triangle-exclamation"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{summary?.total_violations ?? 0}</span>
            <span className="kpi-label">Tổng Sự Vụ Vi Phạm</span>
            <span className="kpi-subtext">
              {summary?.urgent_violations ? `Trong đó có ${summary.urgent_violations} vụ khẩn cấp` : 'Được ghi nhận trong kỳ'}
            </span>
          </div>
        </div>

        {/* KPI 2: Total Rewards */}
        <div className="kpi-card gold-card">
          <div className="kpi-icon-wrap" style={{ background: 'rgba(183, 121, 31, 0.12)', color: '#b7791f' }}>
            <i className="fas fa-award"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{summary?.total_rewards ?? 0}</span>
            <span className="kpi-label">Lượt Khen Thưởng</span>
            <span className="kpi-subtext">
              {summary?.staff_with_rewards ? `${summary.staff_with_rewards} cá nhân được tuyên dương` : 'Ghi nhận thành tích'}
            </span>
          </div>
        </div>

        {/* KPI 3: Compliance Rate */}
        <div className="kpi-card success-card">
          <div className="kpi-icon-wrap" style={{ background: 'rgba(30, 126, 72, 0.12)', color: '#1e7e48' }}>
            <i className="fas fa-shield-halved"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{summary?.compliance_rate ?? 100}%</span>
            <span className="kpi-label">Tỷ Lệ Tuân Thủ Kỷ Luật</span>
            <span className="kpi-subtext">
              {summary?.clean_staff_count ?? 0}/{summary?.total_staff ?? 0} nhân sự không mắc lỗi
            </span>
          </div>
        </div>

        {/* KPI 4: Acknowledgment Rate */}
        <div className="kpi-card primary-card">
          <div className="kpi-icon-wrap" style={{ background: 'rgba(141, 42, 44, 0.12)', color: '#8d2a2c' }}>
            <i className="fas fa-signature"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-value">{summary?.ack_rate ?? 100}%</span>
            <span className="kpi-label">Tỷ Lệ Đã Ký Xác Nhận</span>
            <span className="kpi-subtext">
              {summary?.total_completed_acks ?? 0}/{summary?.total_required_acks ?? 0} biên bản đã ký nhận
            </span>
          </div>
        </div>

        {/* KPI 5: Pending Acks Alert */}
        <div className="kpi-card warning-card">
          <div className="kpi-icon-wrap" style={{ background: 'rgba(217, 119, 6, 0.12)', color: '#d97706' }}>
            <i className="fas fa-bell"></i>
          </div>
          <div className="kpi-info">
            <span className="kpi-value" style={{ color: (summary?.pending_acks_count || 0) > 0 ? '#c0392b' : '#1e7e48' }}>
              {summary?.pending_acks_count ?? 0}
            </span>
            <span className="kpi-label">Biên Bản Chưa Ký</span>
            <span className="kpi-subtext">
              {(summary?.pending_acks_count || 0) > 0 ? 'Cần nhắc nhở nhân sự ký gấp' : 'Tất cả đã hoàn tất ký nhận'}
            </span>
          </div>
        </div>
      </div>

      {/* ANALYTICS ROW: Monthly Trend Chart & Hall of Fame / Warnings */}
      <div className="discipline-analytics-row">
        {/* Monthly Trend SVG Chart */}
        <div className="analytics-card trend-card">
          <div className="analytics-card-header">
            <h3>
              <i className="fas fa-chart-column" style={{ color: '#8d2a2c', marginRight: '8px' }}></i>
              Biểu Đồ Xu Hướng Vi Phạm & Khen Thưởng Qua Các Tháng
            </h3>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-color" style={{ background: '#c0392b' }}></span>
                Vi phạm / Nhắc nhở
              </span>
              <span className="legend-item">
                <span className="legend-color" style={{ background: '#b7791f' }}></span>
                Khen thưởng
              </span>
            </div>
          </div>

          <div className="analytics-chart-container">
            {timelineStats.length === 0 ? (
              <div className="chart-empty-state">Chưa có dữ liệu xu hướng trong khoảng thời gian này.</div>
            ) : (
              <div className="svg-chart-wrapper">
                <svg viewBox="0 0 600 200" className="trend-svg" preserveAspectRatio="none">
                  {/* Grid lines */}
                  <line x1="40" y1="20" x2="580" y2="20" stroke="#f0e9e9" strokeDasharray="3 3" />
                  <line x1="40" y1="70" x2="580" y2="70" stroke="#f0e9e9" strokeDasharray="3 3" />
                  <line x1="40" y1="120" x2="580" y2="120" stroke="#f0e9e9" strokeDasharray="3 3" />
                  <line x1="40" y1="170" x2="580" y2="170" stroke="#d5c8c8" />

                  {timelineStats.map((item, idx) => {
                    const groupWidth = 540 / timelineStats.length;
                    const groupX = 50 + idx * groupWidth;
                    const barW = Math.min(22, groupWidth / 2.8);

                    const vHeight = (item.violations / maxTimelineCount) * 140;
                    const rHeight = (item.rewards / maxTimelineCount) * 140;

                    const vY = 170 - vHeight;
                    const rY = 170 - rHeight;

                    return (
                      <g key={item.period} className="chart-group">
                        {/* Violations bar */}
                        <rect
                          x={groupX + groupWidth / 2 - barW - 2}
                          y={vY}
                          width={barW}
                          height={vHeight}
                          fill="#c0392b"
                          rx="4"
                          className="chart-bar"
                        >
                          <title>{`Tháng ${item.period}: ${item.violations} vi phạm`}</title>
                        </rect>
                        {item.violations > 0 && (
                          <text
                            x={groupX + groupWidth / 2 - barW / 2 - 2}
                            y={vY - 4}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight="bold"
                            fill="#c0392b"
                          >
                            {item.violations}
                          </text>
                        )}

                        {/* Rewards bar */}
                        <rect
                          x={groupX + groupWidth / 2 + 2}
                          y={rY}
                          width={barW}
                          height={rHeight}
                          fill="#b7791f"
                          rx="4"
                          className="chart-bar"
                        >
                          <title>{`Tháng ${item.period}: ${item.rewards} khen thưởng`}</title>
                        </rect>
                        {item.rewards > 0 && (
                          <text
                            x={groupX + groupWidth / 2 + barW / 2 + 2}
                            y={rY - 4}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight="bold"
                            fill="#b7791f"
                          >
                            {item.rewards}
                          </text>
                        )}

                        {/* Month label */}
                        <text
                          x={groupX + groupWidth / 2}
                          y="190"
                          textAnchor="middle"
                          fontSize="11"
                          fill="#726969"
                          fontWeight="500"
                        >
                          {item.period.split('-')[1] ? `T${parseInt(item.period.split('-')[1])}` : item.period}
                        </text>
                      </g>
                    );
                  })}
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Hall of Fame & Top Violations Panel */}
        <div className="analytics-card rankings-card">
          <div className="rankings-columns">
            {/* Column 1: Top 5 Khen Thưởng */}
            <div className="rankings-subcol gold-col">
              <div className="subcol-title">
                <i className="fas fa-trophy" style={{ color: '#b7791f', marginRight: '6px' }}></i>
                Top Vinh Danh
              </div>
              <div className="ranking-list">
                {topCommendedStaff.length === 0 ? (
                  <div className="empty-sublist">Chưa có khen thưởng trong kỳ</div>
                ) : (
                  topCommendedStaff.map((st, i) => (
                    <div
                      key={st.staff_id}
                      className="ranking-item clickable"
                      onClick={() => setSelectedStaffForModal(st)}
                      title="Xem hồ sơ chi tiết"
                    >
                      <span className={`rank-badge rank-${i + 1}`}>{i + 1}</span>
                      <div className="rank-info">
                        <span className="rank-name">{st.staff_name}</span>
                        <span className="rank-role">{st.role || st.staff_id}</span>
                      </div>
                      <span className="rank-count gold-count">
                        <i className="fas fa-award"></i> {st.reward_count}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Column 2: Top 5 Cần Lưu Ý */}
            <div className="rankings-subcol warning-col">
              <div className="subcol-title">
                <i className="fas fa-triangle-exclamation" style={{ color: '#c0392b', marginRight: '6px' }}></i>
                Cần Lưu Ý (Vi Phạm)
              </div>
              <div className="ranking-list">
                {topViolationsStaff.length === 0 ? (
                  <div className="empty-sublist" style={{ color: '#1e7e48' }}>
                    <i className="fas fa-check"></i> Không có nhân viên vi phạm
                  </div>
                ) : (
                  topViolationsStaff.map((st, i) => (
                    <div
                      key={st.staff_id}
                      className="ranking-item clickable"
                      onClick={() => setSelectedStaffForModal(st)}
                      title="Xem hồ sơ chi tiết"
                    >
                      <span className="rank-badge rank-warn">{i + 1}</span>
                      <div className="rank-info">
                        <span className="rank-name">{st.staff_name}</span>
                        <span className="rank-role">{st.role || st.staff_id}</span>
                      </div>
                      <span className="rank-count red-count">
                        <i className="fas fa-exclamation-circle"></i> {st.violation_count}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DUAL VIEW TABS HEADER */}
      <div className="discipline-view-tabs-header">
        <div className="tab-buttons">
          <button
            type="button"
            className={`tab-toggle-btn ${activeTab === 'staff_view' ? 'active' : ''}`}
            onClick={() => setActiveTab('staff_view')}
          >
            <i className="fas fa-users-gear" style={{ marginRight: '8px' }}></i>
            Thống Kê Theo Từng Nhân Viên ({filteredStaffStats.length})
          </button>
          <button
            type="button"
            className={`tab-toggle-btn ${activeTab === 'incidents_view' ? 'active' : ''}`}
            onClick={() => setActiveTab('incidents_view')}
          >
            <i className="fas fa-clipboard-list" style={{ marginRight: '8px' }}></i>
            Nhật Ký Chi Tiết Sự Vụ ({filteredIncidents.length})
          </button>
        </div>

        {activeTab === 'staff_view' && (
          <div className="staff-sort-options">
            <span className="sort-label">Sắp xếp:</span>
            <button
              className={`sort-pill ${staffSortBy === 'violations' ? 'active' : ''}`}
              onClick={() => {
                if (staffSortBy === 'violations') {
                  setStaffSortOrder(staffSortOrder === 'desc' ? 'asc' : 'desc');
                } else {
                  setStaffSortBy('violations');
                  setStaffSortOrder('desc');
                }
              }}
            >
              Nhiều lỗi nhất {staffSortBy === 'violations' && (staffSortOrder === 'desc' ? '↓' : '↑')}
            </button>
            <button
              className={`sort-pill ${staffSortBy === 'rewards' ? 'active' : ''}`}
              onClick={() => {
                if (staffSortBy === 'rewards') {
                  setStaffSortOrder(staffSortOrder === 'desc' ? 'asc' : 'desc');
                } else {
                  setStaffSortBy('rewards');
                  setStaffSortOrder('desc');
                }
              }}
            >
              Nhiều khen thưởng nhất {staffSortBy === 'rewards' && (staffSortOrder === 'desc' ? '↓' : '↑')}
            </button>
            <button
              className={`sort-pill ${staffSortBy === 'name' ? 'active' : ''}`}
              onClick={() => {
                if (staffSortBy === 'name') {
                  setStaffSortOrder(staffSortOrder === 'desc' ? 'asc' : 'desc');
                } else {
                  setStaffSortBy('name');
                  setStaffSortOrder('asc');
                }
              }}
            >
              Tên A-Z {staffSortBy === 'name' && (staffSortOrder === 'asc' ? '↑' : '↓')}
            </button>
          </div>
        )}
      </div>

      {/* TAB 1: STAFF AGGREGATION VIEW */}
      {activeTab === 'staff_view' && (
        <div className="discipline-table-wrap">
          {error && <div className="bulletin-alert-box error">{error}</div>}

          {filteredStaffStats.length === 0 ? (
            <div className="bulletin-empty-state">
              <i className="fas fa-user-slash empty-icon"></i>
              <h3>Không tìm thấy nhân viên phù hợp</h3>
              <p>Thử điều chỉnh bộ lọc hoặc từ khóa tìm kiếm.</p>
            </div>
          ) : (
            <table className="discipline-table">
              <thead>
                <tr>
                  <th>Nhân Viên</th>
                  <th>Bộ Phận</th>
                  <th style={{ textAlign: 'center' }}>Số Lỗi Vi Phạm</th>
                  <th style={{ textAlign: 'center' }}>Khen Thưởng</th>
                  <th style={{ textAlign: 'center' }}>Xác Nhận Biên Bản</th>
                  <th style={{ textAlign: 'center' }}>Xếp Loại Thi Đua</th>
                  <th>Gần Nhất</th>
                  <th style={{ textAlign: 'center' }}>Thao Tác</th>
                </tr>
              </thead>
              <tbody>
                {filteredStaffStats.map((st) => {
                  const evalMeta = getStaffEvaluation(st);
                  const totalAckRequired = st.acknowledged_count + st.unacknowledged_count;
                  const ackPercent = totalAckRequired > 0 ? Math.round((st.acknowledged_count / totalAckRequired) * 100) : 100;

                  return (
                    <tr key={st.staff_id} className="staff-row">
                      {/* Staff Name & Avatar */}
                      <td>
                        <div className="staff-cell-info">
                          <div
                            className="staff-avatar-badge"
                            style={{ backgroundColor: st.color || '#8d2a2c' }}
                          >
                            {st.staff_name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="staff-name-title">{st.staff_name}</div>
                            <div className="staff-id-sub">Mã: {st.staff_id}</div>
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td>
                        <span className="role-tag">{st.role || '-'}</span>
                      </td>

                      {/* Violations count */}
                      <td style={{ textAlign: 'center' }}>
                        {st.violation_count === 0 ? (
                          <span className="count-chip zero-chip">0</span>
                        ) : (
                          <span className={`count-chip violation-chip ${st.violation_count >= 3 ? 'severe' : ''}`}>
                            <i className="fas fa-exclamation-triangle"></i> {st.violation_count}
                            {st.urgent_violation_count > 0 && (
                              <span className="urgent-tag" title="Số vụ khẩn cấp">
                                ({st.urgent_violation_count} khẩn)
                              </span>
                            )}
                          </span>
                        )}
                      </td>

                      {/* Rewards count */}
                      <td style={{ textAlign: 'center' }}>
                        {st.reward_count === 0 ? (
                          <span className="count-chip zero-chip">0</span>
                        ) : (
                          <span className="count-chip reward-chip">
                            <i className="fas fa-award"></i> {st.reward_count}
                          </span>
                        )}
                      </td>

                      {/* Acknowledgment Status */}
                      <td style={{ textAlign: 'center' }}>
                        {totalAckRequired === 0 ? (
                          <span className="ack-status-text muted">Không có</span>
                        ) : st.unacknowledged_count === 0 ? (
                          <div className="ack-status-box success">
                            <i className="fas fa-check-circle"></i> {st.acknowledged_count}/{totalAckRequired} (100%)
                          </div>
                        ) : (
                          <div className="ack-status-box pending" title={`${st.unacknowledged_count} biên bản chưa xác nhận`}>
                            <i className="fas fa-clock"></i> Còn {st.unacknowledged_count} chưa ký ({ackPercent}%)
                          </div>
                        )}
                      </td>

                      {/* Thi Đua Evaluation */}
                      <td style={{ textAlign: 'center' }}>
                        <span
                          className="evaluation-pill"
                          style={{ color: evalMeta.color, background: evalMeta.bg }}
                        >
                          <i className={evalMeta.icon} style={{ marginRight: '5px' }}></i>
                          {evalMeta.label}
                        </span>
                      </td>

                      {/* Latest Incident */}
                      <td>
                        <span className="date-text">{formatDateVN(st.latest_incident_date)}</span>
                      </td>

                      {/* Actions */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn-view-profile"
                          onClick={() => setSelectedStaffForModal(st)}
                        >
                          <i className="fas fa-id-card" style={{ marginRight: '5px' }}></i>
                          Hồ Sơ ({st.total_incidents})
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* TAB 2: DETAILED INCIDENTS LOG VIEW */}
      {activeTab === 'incidents_view' && (
        <div className="discipline-table-wrap">
          {error && <div className="bulletin-alert-box error">{error}</div>}

          {filteredIncidents.length === 0 ? (
            <div className="bulletin-empty-state">
              <i className="fas fa-folder-open empty-icon"></i>
              <h3>Không có sự vụ nào phù hợp</h3>
              <p>Không tìm thấy biên bản vi phạm hoặc khen thưởng nào với bộ lọc này.</p>
            </div>
          ) : (
            <table className="discipline-table incidents-table">
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>Ngày Ban Hành</th>
                  <th style={{ width: '150px' }}>Phân Loại</th>
                  <th style={{ width: '110px' }}>Mức Độ</th>
                  <th>Tiêu Đề & Tóm Tắt</th>
                  <th>Đối Tượng Áp Dụng</th>
                  <th style={{ width: '140px', textAlign: 'center' }}>Đã Xem / Ký Nhận</th>
                  <th style={{ width: '110px', textAlign: 'center' }}>Thao Tác</th>
                </tr>
              </thead>
              <tbody>
                {filteredIncidents.map((inc) => {
                  const catMeta = CATEGORY_META[inc.category as 'vi_pham' | 'khen_thuong'] || {
                    label: inc.category,
                    color: '#8d2a2c',
                    bg: 'rgba(141,42,44,0.1)',
                    icon: 'fas fa-info-circle',
                  };
                  const prioMeta = PRIORITY_META[inc.priority] || { label: inc.priority, color: '#726969' };
                  const percentRead =
                    inc.total_recipients && inc.total_recipients > 0
                      ? Math.round(((inc.read_count || 0) / inc.total_recipients) * 100)
                      : 0;

                  return (
                    <tr key={inc.id} className="incident-row">
                      {/* Date */}
                      <td>
                        <div className="incident-date-cell">
                          <span className="time">{formatDateTimeVN(inc.created_at)}</span>
                        </div>
                      </td>

                      {/* Category Badge */}
                      <td>
                        <span
                          className="incident-cat-badge"
                          style={{ color: catMeta.color, background: catMeta.bg }}
                        >
                          <i className={catMeta.icon} style={{ marginRight: '5px' }}></i>
                          {catMeta.label}
                        </span>
                      </td>

                      {/* Priority */}
                      <td>
                        <span className={`incident-prio-badge prio-${inc.priority}`}>
                          {prioMeta.label}
                        </span>
                      </td>

                      {/* Title & Summary */}
                      <td>
                        <div className="incident-content-cell">
                          <span className="incident-title" onClick={() => setSelectedBulletinForModal(inc)}>
                            {inc.title}
                          </span>
                          {inc.summary && <span className="incident-summary">{inc.summary}</span>}
                          <div className="incident-author">Người lập: {inc.author_name}</div>
                        </div>
                      </td>

                      {/* Target Audience */}
                      <td>
                        {inc.target_type === 'all' ? (
                          <span className="target-pill all">
                            <i className="fas fa-globe"></i> Toàn thể ({inc.total_recipients || 0})
                          </span>
                        ) : (
                          <div className="target-recipients-chips">
                            {(inc.recipient_names || []).map((name, idx) => (
                              <span key={idx} className="recipient-chip">
                                <i className="fas fa-user"></i> {name}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Read & Ack Progress */}
                      <td style={{ textAlign: 'center' }}>
                        <div className="incident-progress-cell">
                          <div className="progress-text">
                            Đã xem: {inc.read_count || 0}/{inc.total_recipients || 0} ({percentRead}%)
                          </div>
                          <div className="progress-track-sm">
                            <div
                              className="progress-fill-sm"
                              style={{
                                width: `${percentRead}%`,
                                backgroundColor: percentRead >= 80 ? '#1e7e48' : percentRead >= 40 ? '#b7791f' : '#c0392b',
                              }}
                            ></div>
                          </div>
                          {inc.require_ack && (
                            <div className="ack-text">
                              <i className="fas fa-signature"></i> Đã ký: {inc.ack_count || 0}/{inc.total_recipients || 0}
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Action */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn-view-incident"
                          onClick={() => setSelectedBulletinForModal(inc)}
                          title="Xem chi tiết biên bản"
                        >
                          <i className="fas fa-eye"></i> Chi tiết
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* =========================================================================
          MODAL 1: STAFF DETAIL TIMELINE (Hồ Sơ Cá Nhân)
          ========================================================================= */}
      {selectedStaffForModal && (
        <div className="bulletin-modal-backdrop" onClick={() => setSelectedStaffForModal(null)}>
          <div className="bulletin-modal-window staff-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bulletin-modal-header">
              <div className="modal-header-staff-profile">
                <div
                  className="staff-profile-avatar"
                  style={{ backgroundColor: selectedStaffForModal.color || '#8d2a2c' }}
                >
                  {selectedStaffForModal.staff_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2>Hồ Sơ Kỷ Luật & Khen Thưởng: {selectedStaffForModal.staff_name}</h2>
                  <div className="staff-meta-row">
                    <span>Mã NV: <strong>{selectedStaffForModal.staff_id}</strong></span>
                    <span>•</span>
                    <span>Bộ phận: <strong>{selectedStaffForModal.role || '-'}</strong></span>
                    <span>•</span>
                    <span>Trạng thái: <strong>{selectedStaffForModal.status}</strong></span>
                  </div>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setSelectedStaffForModal(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="staff-modal-body">
              {/* Summary Stats for this staff */}
              <div className="staff-mini-kpis">
                <div className="mini-kpi-item red">
                  <span className="num">{selectedStaffForModal.violation_count}</span>
                  <span className="lbl">Lỗi Vi Phạm</span>
                </div>
                <div className="mini-kpi-item gold">
                  <span className="num">{selectedStaffForModal.reward_count}</span>
                  <span className="lbl">Khen Thưởng</span>
                </div>
                <div className="mini-kpi-item green">
                  <span className="num">{selectedStaffForModal.acknowledged_count}</span>
                  <span className="lbl">Đã Ký Xác Nhận</span>
                </div>
                <div className="mini-kpi-item orange">
                  <span className="num">{selectedStaffForModal.unacknowledged_count}</span>
                  <span className="lbl">Chưa Ký Nhận</span>
                </div>
              </div>

              {/* Timeline list of incidents */}
              <h3 className="timeline-section-title">
                <i className="fas fa-clock-rotate-left" style={{ marginRight: '8px', color: '#8d2a2c' }}></i>
                Lịch Sử Sự Vụ Đã Ghi Nhận ({selectedStaffForModal.incidents.length})
              </h3>

              {selectedStaffForModal.incidents.length === 0 ? (
                <div className="empty-staff-timeline">
                  <i className="fas fa-shield-heart" style={{ fontSize: '32px', color: '#1e7e48', marginBottom: '8px' }}></i>
                  <p>Nhân viên này chưa có bất kỳ sự vụ vi phạm hay khen thưởng nào được ghi nhận trong hệ thống.</p>
                </div>
              ) : (
                <div className="staff-timeline-feed">
                  {selectedStaffForModal.incidents.map((incItem) => {
                    const isViolation = incItem.category === 'vi_pham';
                    return (
                      <div
                        key={incItem.id}
                        className={`timeline-feed-item ${isViolation ? 'is-violation' : 'is-reward'}`}
                      >
                        <div className="feed-dot">
                          <i className={isViolation ? 'fas fa-exclamation' : 'fas fa-award'}></i>
                        </div>
                        <div className="feed-card">
                          <div className="feed-card-top">
                            <div className="feed-tags">
                              <span className={`feed-cat-badge ${isViolation ? 'danger' : 'gold'}`}>
                                {isViolation ? 'Vi phạm / Nhắc nhở' : 'Khen thưởng / Vinh danh'}
                              </span>
                              {incItem.priority === 'urgent' && (
                                <span className="feed-prio-urgent">Khẩn cấp</span>
                              )}
                              {incItem.priority === 'important' && (
                                <span className="feed-prio-important">Quan trọng</span>
                              )}
                            </div>
                            <span className="feed-date">{formatDateTimeVN(incItem.created_at)}</span>
                          </div>

                          <h4 className="feed-title">{incItem.title}</h4>
                          {incItem.summary && <p className="feed-summary">{incItem.summary}</p>}
                          <div className="feed-content-snippet">{incItem.content}</div>

                          {/* Media attachments */}
                          {incItem.media_files && incItem.media_files.length > 0 && (
                            <div className="feed-attachments">
                              <span className="attach-lbl">Đính kèm ({incItem.media_files.length} tệp):</span>
                              <div className="attach-list">
                                {incItem.media_files.map((file, fIdx) => (
                                  <a
                                    key={fIdx}
                                    href={file.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="attach-link-chip"
                                  >
                                    <i className="fas fa-paperclip"></i> {file.originalName || file.fileName}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Personal Read & Ack Status */}
                          <div className="feed-footer">
                            <div className="feed-user-tracking">
                              <span className={`track-pill ${incItem.is_read ? 'read' : 'unread'}`}>
                                <i className={`fas ${incItem.is_read ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                                {incItem.is_read ? `Đã xem (${formatDateTimeVN(incItem.read_at)})` : 'Chưa xem'}
                              </span>

                              {incItem.require_ack && (
                                <span
                                  className={`track-pill ${incItem.is_acknowledged ? 'acknowledged' : 'pending'}`}
                                >
                                  <i className={`fas ${incItem.is_acknowledged ? 'fa-signature' : 'fa-clock'}`}></i>
                                  {incItem.is_acknowledged
                                    ? `Đã ký nhận (${formatDateTimeVN(incItem.acknowledged_at)})`
                                    : 'Chưa ký xác nhận'}
                                </span>
                              )}
                            </div>

                            <span className="feed-author">Người lập: {incItem.author_name}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bulletin-modal-footer">
              <button className="btn-modal-close" onClick={() => setSelectedStaffForModal(null)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL 2: BULLETIN INCIDENT DETAIL
          ========================================================================= */}
      {selectedBulletinForModal && (
        <div className="bulletin-modal-backdrop" onClick={() => setSelectedBulletinForModal(null)}>
          <div className="bulletin-modal-window detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bulletin-modal-header">
              <h2>Chi Tiết Biên Bản / Quyết Định</h2>
              <button className="modal-close-btn" onClick={() => setSelectedBulletinForModal(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="detail-modal-body">
              <div className="detail-meta-header">
                <span
                  className="incident-cat-badge"
                  style={{
                    color: selectedBulletinForModal.category === 'vi_pham' ? '#c0392b' : '#b7791f',
                    background:
                      selectedBulletinForModal.category === 'vi_pham'
                        ? 'rgba(192, 57, 43, 0.1)'
                        : 'rgba(183, 121, 31, 0.12)',
                  }}
                >
                  <i
                    className={
                      selectedBulletinForModal.category === 'vi_pham'
                        ? 'fas fa-exclamation-triangle'
                        : 'fas fa-award'
                    }
                    style={{ marginRight: '6px' }}
                  ></i>
                  {selectedBulletinForModal.category === 'vi_pham' ? 'Vi phạm / Nhắc nhở' : 'Khen thưởng / Vinh danh'}
                </span>
                <span className="detail-date">{formatDateTimeVN(selectedBulletinForModal.created_at)}</span>
              </div>

              <h2 className="detail-title">{selectedBulletinForModal.title}</h2>
              {selectedBulletinForModal.summary && (
                <div className="detail-summary-quote">{selectedBulletinForModal.summary}</div>
              )}

              <div className="detail-content-text" style={{ whiteSpace: 'pre-wrap', marginTop: '16px' }}>
                {selectedBulletinForModal.content}
              </div>

              {/* Media Attachments */}
              {selectedBulletinForModal.media_files && selectedBulletinForModal.media_files.length > 0 && (
                <div className="detail-media-gallery" style={{ marginTop: '20px' }}>
                  <h4>Tệp Đính Kèm ({selectedBulletinForModal.media_files.length}):</h4>
                  <div className="media-chips-container">
                    {selectedBulletinForModal.media_files.map((file, idx) => (
                      <a
                        key={idx}
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="attach-link-chip"
                      >
                        <i className="fas fa-download"></i> {file.originalName || file.fileName}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Target & Tracking Info */}
              <div className="detail-tracking-summary" style={{ marginTop: '24px' }}>
                <h4>
                  <i className="fas fa-users" style={{ marginRight: '8px' }}></i>
                  Đối Tượng Áp Dụng:
                </h4>
                {selectedBulletinForModal.target_type === 'all' ? (
                  <p>Áp dụng cho toàn thể nhân viên</p>
                ) : (
                  <div className="target-recipients-chips">
                    {(selectedBulletinForModal.recipient_names || []).map((name, idx) => (
                      <span key={idx} className="recipient-chip">
                        <i className="fas fa-user-check"></i> {name}
                      </span>
                    ))}
                  </div>
                )}
                <div className="tracking-stats-badge" style={{ marginTop: '10px' }}>
                  Đã xem: {selectedBulletinForModal.read_count || 0}/{selectedBulletinForModal.total_recipients || 0}
                  {selectedBulletinForModal.require_ack && (
                    <span> • Đã ký nhận: {selectedBulletinForModal.ack_count || 0}/{selectedBulletinForModal.total_recipients || 0}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="bulletin-modal-footer">
              <button className="btn-modal-close" onClick={() => setSelectedBulletinForModal(null)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================================
          MODAL 3: QUICK CREATE VIOLATION / REWARD
          ========================================================================= */}
      {isQuickCreateOpen && (
        <div className="bulletin-modal-backdrop" onClick={() => setIsQuickCreateOpen(false)}>
          <div className="bulletin-modal-window" onClick={(e) => e.stopPropagation()}>
            <div className="bulletin-modal-header">
              <h2>
                <i className="fas fa-pen-nib" style={{ marginRight: '8px' }}></i>
                Lập Ghi Nhận Kỷ Luật / Khen Thưởng
              </h2>
              <button className="modal-close-btn" onClick={() => setIsQuickCreateOpen(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleQuickCreateSubmit} className="bulletin-form">
              <div className="bulletin-form-body">
                {/* Category & Priority */}
                <div className="form-row-2">
                  <div className="form-group">
                    <label>
                      Phân loại sự vụ <span className="req">*</span>
                    </label>
                    <select
                      value={createCategory}
                      onChange={(e) => setCreateCategory(e.target.value as 'vi_pham' | 'khen_thuong')}
                      required
                    >
                      <option value="vi_pham">⚠️ Vi phạm / Kỷ luật / Nhắc nhở</option>
                      <option value="khen_thuong">🏆 Khen thưởng / Vinh danh</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Mức độ ưu tiên</label>
                    <select
                      value={createPriority}
                      onChange={(e) => setCreatePriority(e.target.value as BulletinPriority)}
                    >
                      <option value="normal">Bình thường</option>
                      <option value="important">Quan trọng (Nổi bật)</option>
                      <option value="urgent">Khẩn cấp (Tự động bật popup)</option>
                    </select>
                  </div>
                </div>

                {/* Target Audience */}
                <div className="form-group target-selector-group">
                  <label>
                    Nhân viên áp dụng <span className="req">*</span>
                  </label>
                  <div className="target-type-segmented">
                    <button
                      type="button"
                      className={`seg-btn ${createTargetType === 'specific' ? 'active' : ''}`}
                      onClick={() => setCreateTargetType('specific')}
                    >
                      <i className="fas fa-user-check"></i> Chỉ định từng nhân viên ({createRecipientIds.length} đã chọn)
                    </button>
                    <button
                      type="button"
                      className={`seg-btn ${createTargetType === 'all' ? 'active' : ''}`}
                      onClick={() => setCreateTargetType('all')}
                    >
                      <i className="fas fa-globe"></i> Toàn bộ nhân viên
                    </button>
                  </div>

                  {createTargetType === 'specific' && (
                    <div className="specific-staff-box" style={{ marginTop: '12px' }}>
                      <div className="staff-box-header">
                        <div className="staff-search-input">
                          <i className="fas fa-search"></i>
                          <input
                            type="text"
                            placeholder="Tìm nhân viên theo tên, mã hoặc chức vụ..."
                            value={createStaffSearch}
                            onChange={(e) => setCreateStaffSearch(e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="staff-grid-picker" style={{ maxHeight: '160px', overflowY: 'auto' }}>
                        {filteredCreateStaffList.map((st) => {
                          const isSelected = createRecipientIds.includes(st.id);
                          return (
                            <div
                              key={st.id}
                              className={`staff-pick-item ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleToggleRecipient(st.id)}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                              />
                              <div className="pick-info">
                                <span className="pick-name">{st.name}</span>
                                <span className="pick-code">{st.id} {st.role ? `• ${st.role}` : ''}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div className="form-group">
                  <label>
                    Tiêu đề ghi nhận <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder={
                      createCategory === 'vi_pham'
                        ? 'VD: Biên bản nhắc nhở vi phạm quy chế chấm công ngày...'
                        : 'VD: Quyết định khen thưởng hoàn thành xuất sắc nhiệm vụ...'
                    }
                    value={createTitle}
                    onChange={(e) => setCreateTitle(e.target.value)}
                    required
                  />
                </div>

                {/* Summary */}
                <div className="form-group">
                  <label>Tóm tắt lý do</label>
                  <input
                    type="text"
                    placeholder="Tóm tắt ngắn gọn 1-2 câu..."
                    value={createSummary}
                    onChange={(e) => setCreateSummary(e.target.value)}
                  />
                </div>

                {/* Content */}
                <div className="form-group">
                  <label>
                    Nội dung chi tiết biên bản / quyết định <span className="req">*</span>
                  </label>
                  <textarea
                    rows={5}
                    placeholder="Mô tả chi tiết hành vi, thời gian, biện pháp xử lý hoặc mức thưởng/phạt..."
                    value={createContent}
                    onChange={(e) => setCreateContent(e.target.value)}
                    required
                  />
                </div>

                {/* Require Acknowledgment */}
                <div className="form-group">
                  <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={createRequireAck}
                      onChange={(e) => setCreateRequireAck(e.target.checked)}
                    />
                    <span>
                      <strong>Yêu cầu nhân viên ký xác nhận (Đã hiểu và đồng ý)</strong>
                    </span>
                  </label>
                </div>
              </div>

              <div className="bulletin-modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsQuickCreateOpen(false)}
                  disabled={isCreating}
                >
                  Hủy bỏ
                </button>
                <button type="submit" className="bulletin-btn-create" disabled={isCreating}>
                  {isCreating ? 'Đang lưu...' : 'Ban Hành Ngay'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
